import React, { useRef, useState, useEffect } from 'react';
import { generateUniquePrefixForProject, createProject } from '../../utils/api/project_apis';
import { STATUS } from '../../utils/api/sharedfunction';
import ModalWrapper from '../ModalWrapper';
import { FormSchemas, FormWrapper } from '../FormComponent';
import CommonFooter from '../FormComponent/CommonFooter';
import { CircularProgress } from '@mui/material';

const CreateProjectModal = ({ open, onClose, onCreate, onError, projects }) => {
  const debounceTimer = useRef(null);
  const controllerRef = useRef(null);
  const [isGeneratingKey, setIsGeneratingKey] = useState(false);
  const [loading, setLoading] = useState(false);
  const formRef = useRef(null);

  const onCloseHandler = () => {
    onClose();
    setIsGeneratingKey(false);
    if (controllerRef.current) controllerRef.current.abort();
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
  }

  const handleCreateProject = async (data) => {
    try {
      setLoading(true);
      const result = await createProject({
        name: data.projectName,
        description: data.projectDescription,
        context: data.projectContext,
        prefix: data.prefix
      });

      if (result.status === STATUS.SUCCESS) {
        onCreate(result.data);
        onCloseHandler();
      } else {
        onError('error create', result.error || 'Failed to create project');
      }
    } catch (error) {
      onError('error create', error?.message || 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  const generatePrefix = async () => {
    const data = formRef.current.getValues();
    const { projectName } = data;
    if (!projectName || !projectName.trim()) return;
    if (controllerRef.current) controllerRef.current.abort();

    controllerRef.current = new AbortController();
    setIsGeneratingKey(true);

    const validateData = await generateUniquePrefixForProject({
      projectName,
    });
    setIsGeneratingKey(false);
    if (validateData.status === STATUS.ERROR) {
      onError('error validate', validateData?.error);
    } else if (validateData?.status === STATUS.SUCCESS) {
      formRef.current.setValue('prefix', validateData.data);
    }
  }

  const handleFieldChange = () => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(generatePrefix, 500);
  };

  const fields = [
    {
      name: 'projectName',
      type: 'text',
      label: 'Project Name',
      placeholder: 'Enter your full project name',
      required: true,
      customActionOnChange: handleFieldChange,
    },
    {
      name: 'prefix',
      type: 'text',
      label: 'Key',
      placeholder: 'Enter your key',
      required: true,
      helperText: 'Key should be unique, 2-4 characters.',
      maxLength: 4,
      endIcon: isGeneratingKey && <CircularProgress  className='icon primary' />,
    },
    {
      name: 'projectDescription',
      type: 'text',
      label: 'Project Description',
      placeholder: 'Enter your project description',
      multiline: true,
      rows: 2
    },
    {
      name: 'projectContext',
      type: 'text',
      label: 'Project Context',
      placeholder: 'Enter your project context',
      multiline: true,
      rows: 2
    }
  ]

  const handleFormSubmit = async () => {
    if (formRef.current) await formRef.current.submit();
  };

  const buttons = [
    {
      label: 'Cancel',
      variant: 'outline',
      className: 'primary',
      onClick: onCloseHandler,
      disabled: loading,
    },
    {
      label: 'Add',
      variant: 'primary',
      className: 'white bg-blue600',
      onClick: handleFormSubmit,
      disabled: loading,
      loading: loading,
    },
  ];

  return (
    <ModalWrapper
      isOpen={open}
      onClose={onCloseHandler}
      title="Create New Project"
      size="small"
      footer={
        <CommonFooter
          buttons={buttons}
          align="right"
        />
      }
      children={
        <FormWrapper
          ref={formRef}
          schema={FormSchemas.projectForm(true)}
          fields={fields}
          onSubmit={handleCreateProject}
          defaultValues={{
            projectName: '',
            prefix: '',
            projectDescription: '',
            projectContext: ''
          }}
        />
      }
    />
  );
};

export default CreateProjectModal;